import { _isEmptyString, _notNil, uuid } from '@/littledash';
import type { AnimalAlertFrom, AnimalAlertSpecCreateOrUpdate, AnimalAlertV1, StudyAlertV1 } from '@/model/Alert.model';
import type { ID } from '@/model/Common.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import { DateUtils } from '@/utils/Date.utils.ts';
import { roundToTwo } from '@/helpers.tsx';
import { consumptionMetrics } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import type { CreateStudyState } from '../../Create.model';

const isUsableId = (id: ID | null | undefined): id is ID => _notNil(id) && !_isEmptyString(id);

export const ensureAlertId = (alert: AnimalAlertSpecCreateOrUpdate): AnimalAlertSpecCreateOrUpdate => ({
  ...alert,
  id: isUsableId(alert.id) ? alert.id : uuid(),
});

export const alertsToMap = (
  alerts: Array<AnimalAlertSpecCreateOrUpdate>
): Map<AnimalAlertSpecCreateOrUpdate['id'], AnimalAlertSpecCreateOrUpdate> =>
  new Map((alerts ?? []).map(ensureAlertId).map((alert) => [alert.id, alert]));

/**
 * Resolves the active preset id from study state, preferring the in-form selection
 * over the persisted study value. Empty strings are treated as absent (a common
 * RHF v7 default-value pitfall) so callers don't end up looking up `Number('')` → 0.
 */
export const resolvePresetId = (state: CreateStudyState | undefined): ID | null => {
  const selectedId = state?.form?.selectedPreset?.id;
  if (isUsableId(selectedId)) {
    return selectedId;
  }
  const studyId = state?.study?.preset_id;
  if (isUsableId(studyId)) {
    return studyId;
  }
  return null;
};

/**
 * Builds the alert-calculation list shown in the Alerts step:
 *  - calculations from the matching preset (if any)
 *  - plus consumption metrics when consumption tracking is enabled
 */
export const resolveCalculations = (state: CreateStudyState | undefined): Array<PresetCalculation> => {
  const presetId = resolvePresetId(state);
  const presetCalculations = _notNil(presetId)
    ? ((state?.form?.presets ?? []).find((p) => Number(p.id) === Number(presetId))?.calculations ?? [])
    : [];
  return state?.study?.enable_consumption === true
    ? [...presetCalculations, ...consumptionMetrics]
    : presetCalculations;
};

export const operators: Array<{ label: string; value: AnimalAlertV1['operator'] }> = [
  {
    label: 'is greater than',
    value: 'GREATER_THAN',
  },
  {
    label: 'is less than',
    value: 'LESS_THAN',
  },
  {
    label: 'has increased by',
    value: 'INCREASES_BY',
  },
  {
    label: 'has decreased by',
    value: 'DECREASES_BY',
  },
];

export const types = (unit: string): Array<{ label: string; value: AnimalAlertV1['type'] }> => [
  {
    label: unit,
    value: 'FIXED',
  },
  {
    label: '%',
    value: 'PERCENT',
  },
];

export const compare: Array<{ label: string; value: AnimalAlertFrom }> = [
  {
    label: 'first',
    value: 'FIRST',
  },
  {
    label: 'last',
    value: 'PREVIOUS',
  },
  {
    label: 'maximum',
    value: 'MAXIMUM',
  },
];

const comparedTo: Record<AnimalAlertFrom, string> = {
  FIRST: ' compared to the first measurement',
  PREVIOUS: ' compared to the last measurement',
  MAXIMUM: ' compared to the max recorded measurement',
};

type AlertConditionSpec = Pick<StudyAlertV1, 'operator' | 'value' | 'type' | 'from' | 'action'> &
  Partial<Pick<AnimalAlertV1, 'comparison_value' | 'comparison_date'>>;

const alertConditionText = (
  alert: AlertConditionSpec,
  calculation: PresetCalculation,
  displayCalculationName: boolean,
  displayAction: boolean,
  includeComparisonDetails: boolean
): JSX.Element | '-' => {
  let alertComparedTo = '';
  const operatorLabel = operators?.find(({ value }) => value === alert?.operator)?.label;
  if (_notNil(operatorLabel)) {
    const valueWithUnit =
      alert?.type === 'PERCENT' ? `${alert.value}%` : `${alert.value} ${calculation.unit ?? ''}`.trim();
    alertComparedTo = _notNil(alert?.from) ? (comparedTo[alert.from] ?? '') : '';

    if (
      includeComparisonDetails &&
      alert.from === 'MAXIMUM' &&
      _notNil(alert.comparison_value) &&
      _notNil(alert.comparison_date)
    ) {
      alertComparedTo = `${alertComparedTo}: ${roundToTwo(alert.comparison_value)}${(calculation.unit ?? '').trim()} (${DateUtils.renderDate(alert.comparison_date)})`;
    }

    return (
      <>
        <div>
          {displayCalculationName && <span className="fw5">{calculation.name} </span>}
          {operatorLabel} {valueWithUnit} {alertComparedTo}
        </div>
        {_notNil(alert?.action) && displayAction && (
          <div>
            <span className="fw5">Action: </span> {alert.action}
          </div>
        )}
      </>
    );
  }
  return '-';
};

interface StudyAlertConditionTextProps {
  alert?: AlertConditionSpec;
  calculation?: PresetCalculation;
  displayCalculationName?: boolean;
  displayAction?: boolean;
}

export const studyAlertConditionText = ({
  alert,
  calculation,
  displayCalculationName = true,
  displayAction = true,
}: StudyAlertConditionTextProps): JSX.Element | '-' => {
  if (_notNil(calculation) && _notNil(alert)) {
    return alertConditionText(alert, calculation, displayCalculationName, displayAction, false);
  }
  return '-';
};

interface AnimalAlertConditionTextProps {
  alert?: AnimalAlertV1;
  calculation?: PresetCalculation;
  displayCalculationName?: boolean;
  displayAction?: boolean;
}

export const animalAlertConditionText = ({
  alert,
  calculation,
  displayCalculationName = true,
  displayAction = true,
}: AnimalAlertConditionTextProps): JSX.Element | '-' => {
  if (_notNil(calculation) && _notNil(alert)) {
    return alertConditionText(alert, calculation, displayCalculationName, displayAction, true);
  }
  return '-';
};
